import type { ControlMsg, HistoryItem } from "./types";

export const CHUNK_SIZE = 16 * 1024;
export const ID_BYTES = 16;

const enc = new TextEncoder();
const dec = new TextDecoder();

export function newId(): string {
  const bytes = new Uint8Array(8);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

export function frameChunk(id: string, payload: Uint8Array): ArrayBuffer {
  if (id.length !== ID_BYTES) {
    throw new Error(`id must be ${ID_BYTES} chars, got ${id.length}`);
  }
  const out = new Uint8Array(ID_BYTES + payload.byteLength);
  out.set(enc.encode(id), 0);
  out.set(payload, ID_BYTES);
  return out.buffer;
}

export function parseFrame(buf: ArrayBuffer): { id: string; payload: Uint8Array } {
  if (buf.byteLength < ID_BYTES) {
    throw new Error("frame too small");
  }
  const view = new Uint8Array(buf);
  const id = dec.decode(view.subarray(0, ID_BYTES));
  const payload = view.subarray(ID_BYTES);
  return { id, payload };
}

interface IncomingFile {
  id: string;
  name: string;
  size: number;
  mime: string;
  received: number;
  chunks: Uint8Array[];
}

export interface TransferOpts {
  sendControl: (msg: ControlMsg) => void;
  sendBulk: (buf: ArrayBuffer) => boolean;
  waitForDrain: () => Promise<void>;
  onItemAdded: (item: HistoryItem) => void;
  onItemUpdate: (id: string, patch: Partial<HistoryItem>) => void;
}

export class TransferEngine {
  private incoming = new Map<string, IncomingFile>();

  constructor(private opts: TransferOpts) {}

  sendText(body: string): void {
    const id = newId();
    const at = Date.now();
    this.opts.onItemAdded({
      kind: "text",
      id,
      direction: "sent",
      body,
      at,
    });
    this.opts.sendControl({ kind: "text", id, body });
  }

  async sendFile(file: File): Promise<void> {
    const id = newId();
    const at = Date.now();
    this.opts.onItemAdded({
      kind: "file",
      id,
      direction: "sent",
      name: file.name,
      size: file.size,
      mime: file.type || "application/octet-stream",
      bytes: 0,
      at,
    });
    this.opts.sendControl({
      kind: "file-start",
      id,
      name: file.name,
      size: file.size,
      mime: file.type || "application/octet-stream",
    });

    let offset = 0;
    while (offset < file.size) {
      const slice = file.slice(offset, offset + CHUNK_SIZE);
      const buf = await slice.arrayBuffer();
      const frame = frameChunk(id, new Uint8Array(buf));
      const ok = this.opts.sendBulk(frame);
      offset += buf.byteLength;
      this.opts.onItemUpdate(id, { bytes: offset } as Partial<HistoryItem>);
      if (!ok) await this.opts.waitForDrain();
    }

    this.opts.sendControl({ kind: "file-end", id });
  }

  handleControl(raw: string): void {
    let msg: ControlMsg;
    try {
      msg = JSON.parse(raw) as ControlMsg;
    } catch {
      return;
    }
    switch (msg.kind) {
      case "text":
        this.opts.onItemAdded({
          kind: "text",
          id: msg.id,
          direction: "received",
          body: msg.body,
          at: Date.now(),
        });
        break;
      case "file-start":
        this.incoming.set(msg.id, {
          id: msg.id,
          name: msg.name,
          size: msg.size,
          mime: msg.mime,
          received: 0,
          chunks: [],
        });
        this.opts.onItemAdded({
          kind: "file",
          id: msg.id,
          direction: "received",
          name: msg.name,
          size: msg.size,
          mime: msg.mime,
          bytes: 0,
          at: Date.now(),
        });
        break;
      case "file-end": {
        const f = this.incoming.get(msg.id);
        if (!f) return;
        const blob = new Blob(f.chunks as BlobPart[], { type: f.mime });
        this.opts.onItemUpdate(msg.id, {
          bytes: f.received,
          blob,
        } as Partial<HistoryItem>);
        this.incoming.delete(msg.id);
        break;
      }
      case "ack":
        break;
    }
  }

  handleBulk(buf: ArrayBuffer): void {
    const { id, payload } = parseFrame(buf);
    const f = this.incoming.get(id);
    if (!f) return;
    f.chunks.push(payload.slice());
    f.received += payload.byteLength;
    this.opts.onItemUpdate(id, { bytes: f.received } as Partial<HistoryItem>);
  }
}
